import { computed, makeObservable } from 'mobx';
import { StarRouter } from './router/StarRouter';
import { BannersBoxState } from './bannersBoxState/BannersBoxState';
import { HeroWidgetState } from './heroWidgetState/HeroWidgetState';
import { NetworkConnectionState } from './networkConnectionState/NetworkConnectionState';
import { PopupState } from './popupState/PopupState';
import { NotificationsState } from 'src/domains/layouts/state/notificationsState/NotificationsState';
import { SportsList } from 'src/domains/layouts/state/sportsList/SportsList';
import { NavigationFavourites } from 'src/domains/layouts/state/navigationFavourites/NavigationFavourites';
import { RecentlyVisitedLocalStorage } from 'src/domains/layouts/state/localStorage/RecentlyVisitedLocalStorage';
import { Overlay } from 'src/domains/layouts/state/overlay/Overlay';
import { IsStaticState } from 'src/domains/layouts/state/isStaticState/IsStaticState';
import { SearchState } from 'src/domains/layouts/webview/components/search/Search.state';
import { PromotedEventsState } from 'src/domains/layouts/webview/components/promotedSports/PromotedEventsState.state';
import { RealityCheckPopupState } from 'src/domains/layouts/state/popups/RealityCheckPopupState';
import { FormatType, ImageOptimizeOptions, imageOptimizedUrl } from 'src_common/common/imageOptimization';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { XtremePushState } from '../webview/components/xtremePush/XtremePush.state';

export class AppLayoutsState {
    public readonly bannersBoxState: BannersBoxState;
    public readonly heroWidgetState: HeroWidgetState;
    public readonly networkConnectionState: NetworkConnectionState;
    public readonly popupState: PopupState;
    public readonly xtremePushState: XtremePushState;
    public readonly overlay: Overlay;
    public readonly isStatic: IsStaticState;
    public readonly notificationsState: NotificationsState;
    public readonly sportsList: SportsList;
    public readonly navigationFavourites: NavigationFavourites;
    public readonly recentlyVisitedLocalStorage: RecentlyVisitedLocalStorage;

    public readonly searchState: SearchState;
    public readonly promotedEventsState: PromotedEventsState;

    public readonly realityCheckPopupState: RealityCheckPopupState;

    public static get = AutoWeakMap.create((common: Common) => new AppLayoutsState(common));

    private constructor(private readonly common: Common) {
        makeObservable(this);

        const starRouter = StarRouter.get(common);
        this.bannersBoxState = BannersBoxState.get(common);
        this.heroWidgetState = new HeroWidgetState(common);
        this.networkConnectionState = NetworkConnectionState.get(common);
        const { config } = ConfigComponents.get(common);

        this.realityCheckPopupState = RealityCheckPopupState.get(common);

        this.popupState = PopupState.get(common);

        this.notificationsState = NotificationsState.get(common);

        this.sportsList = new SportsList(common);
        this.navigationFavourites = new NavigationFavourites(this.sportsList);
        this.recentlyVisitedLocalStorage = new RecentlyVisitedLocalStorage(common, config.navigationRecentlyViewed);

        this.overlay = Overlay.get(common);
        this.searchState = SearchState.get(common);

        this.isStatic = IsStaticState.get(common);

        this.xtremePushState = new XtremePushState(common, this.popupState);

        this.promotedEventsState = new PromotedEventsState(common);

        starRouter.onChangeCurrentView((prevCurrentView, nextCurrentView) => {
            const prevView = prevCurrentView ?? null;
            const nextView = nextCurrentView ?? null;

            if (prevView !== null && nextView !== null && prevView !== nextView) {
                this.notificationsState.onHideAll();
            }
        });

        starRouter.onChangeCurrentView((prevCurrentView, nextCurrentView) => {
            if (prevCurrentView !== null && nextCurrentView !== null && prevCurrentView !== nextCurrentView) {
                this.recentlyVisitedLocalStorage.setItem(nextCurrentView);
            }
        });

        starRouter.onChangeAccountView((_prevView, nextView) => {
            if (nextView?.account === 'signup' && common.session.isAuthorized) {
                starRouter.closeAccount();
            }
        });
    }

    @computed public get isStaticForView(): boolean {
        return this.isStatic.isStatic;
    }

    public optimizeImage = (
        imgSrc: string,
        format: FormatType,
        optimizeOptions?: ImageOptimizeOptions
    ): string | null => {
        const params = {
            imgSrc,
            format,
            ...optimizeOptions,
            quality: optimizeOptions?.quality ?? 85,
        };

        const { imageOptimization } = ConfigComponents.get(this.common).config;

        if (imageOptimization === null) {
            return null;
        }

        if (imageOptimization.type === 'baseUrl') {
            return imageOptimizedUrl(params, imageOptimization.url, imageOptimization.usePublicGoogleUrls);
        }
        return imageOptimizedUrl(params, null, imageOptimization.usePublicGoogleUrls);
    };
}
